import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HxProductInfoService, KindType, ProductInfoModel, ProductInfoType, UiLabel } from 'hx-services';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { takeUntil } from 'rxjs/operators';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import {
  HxBrandSelectComponent,
  HxDictionarySelectComponent,
  HxEnumSelectComponent,
  HxLabelEditComponent,
  HxProductCategorySelectComponent,
  HxUnitMeasureSelectComponent
} from 'hx-components';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NgFor, NgIf } from '@angular/common';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzInputDirective } from 'ng-zorro-antd/input';

interface FormModel {
  enabled: boolean;
  title: UiLabel;
  brandId: number;
  categoryId: number;
  kind: KindType;
  type: ProductInfoType;
  fullTitle: UiLabel;
  composition: UiLabel;
  description: UiLabel;
  decored: boolean;
  globalForSale: boolean;
  cfOutputProduct: boolean;
  toCbInvoice: boolean;
  dynamicWeight: boolean;
  onlineSellable: boolean;
  purchasable: boolean;
  unlimited: boolean;
  weight: number;
  unitOfMeasureId: number;
  spendCategoryCode: string;
  ntin: string;
  gtin: string;
}

@Component({
    selector: 'hc-product-definition-edit',
    templateUrl: './product-definition-edit.component.html',
    styleUrls: ['./product-definition-edit.component.less'],
  imports: [TranslocoDirective, NzSkeletonComponent, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxBrandSelectComponent, HxEnumSelectComponent, NzRadioGroupComponent, NzRadioComponent, HxLabelEditComponent, NgIf, HxProductCategorySelectComponent, NgFor, NzInputNumberComponent, HxUnitMeasureSelectComponent, HxDictionarySelectComponent, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, TranslocoPipe, NzInputDirective]
})
export class ProductDefinitionEditComponent implements OnInit, OnDestroy {
  @Input() model!: ProductInfoModel;
  @Input() isEdit = false;
  @Input() isCreate = false;
  form!: UntypedFormGroup;
  isLoading = {
    model: false,
  };
  isSaving = false;
  KindType = KindType;
  ProductInfoType = ProductInfoType;
  isInventoryItems = false;
  isNotMaterial = false;
  private readonly path = 'product-definitions';
  private $destroyed = new Subject<void>();

  constructor(
    protected aRoute: ActivatedRoute,
    protected fb: UntypedFormBuilder,
    protected productService: HxProductInfoService,
    protected router: Router,
    protected modal: NzModalService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
  ) {
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      enabled: [true, [Validators.required]],
      title: [null, [Validators.required]],
      brandId: [null, [Validators.required]],
      type: [null, [Validators.required]],
      kind: [null, [Validators.required]],
      fullTitle: [null, [Validators.required]],
      description: [null, [Validators.required]],
      decored: [null, [Validators.required]],
      globalForSale: [null, [Validators.required]],
      cfOutputProduct: [null, [Validators.required]],
      toCbInvoice: [null, [Validators.required]],
      dynamicWeight: [null, [Validators.required]],
      onlineSellable: [null, [Validators.required]],
      purchasable: [null, [Validators.required]],
      unlimited: [null, [Validators.required]],
      weight: [null],
      unitOfMeasureId: [null],
      composition: [null],
      categoryId: [null],
      spendCategoryCode: [null],
      ntin: [''],
      gtin: [''],
    });
    this.form.controls['type'].valueChanges.pipe(takeUntil(this.$destroyed)).subscribe(type => {
      this.isInventoryItems = ['INGREDIENT', 'PRODUCT', 'PROPERTY'].includes(type);
      if (this.isInventoryItems) {
        this.form.controls['weight'].setValidators(Validators.required);
        this.form.controls['unitOfMeasureId'].setValidators(Validators.required);
        this.form.controls['composition'].setValidators(Validators.required);
      } else {
        this.form.controls['weight'].setValidators(null);
        this.form.controls['unitOfMeasureId'].setValidators(null);
        this.form.controls['composition'].setValidators(null);
      }
      this.isNotMaterial = !(['MATERIAL_ASSETS', 'NON_MATERIAL_ASSETS'].includes(type));
      if (this.isNotMaterial) {
        this.form.controls['categoryId'].setValidators(Validators.required);
      } else {
        this.form.controls['categoryId'].setValidators(null);
      }
    });
    this.updateForm();
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.getRawValue() as FormModel;
      const model: Partial<ProductInfoModel> = {
        id: this.model.id,
        enabled: formModel.enabled,
        title: formModel.title,
        brandId: formModel.brandId,
        categoryId: formModel.categoryId,
        kind: formModel.kind,
        type: formModel.type,
        fullTitle: formModel.fullTitle,
        composition: formModel.composition,
        description: formModel.description,
        decored: formModel.decored,
        globalForSale: formModel.globalForSale,
        cfOutputProduct: formModel.cfOutputProduct,
        toCbInvoice: formModel.toCbInvoice,
        dynamicWeight: formModel.dynamicWeight,
        onlineSellable: formModel.onlineSellable,
        purchasable: formModel.purchasable,
        unlimited: formModel.unlimited,
        weight: formModel.weight ?? null,
        unitOfMeasureId: formModel.unitOfMeasureId,
        spendCategoryCode: formModel.spendCategoryCode,
        ntin: formModel.ntin,
        gtin: formModel.gtin
      };

      this.isSaving = true;
      this.productService.saveProductInfo(model as ProductInfoModel).subscribe(result => {
        this.form.markAsPristine();
        this.notification.create(
          'success',
          this.tr.translate('nav.ProductDefinitions'),
          this.tr.translate('nt.dataSaved')
        );
        if (this.isCreate) {
          this.router.navigate([`/${this.path}/${result.id}/edit`], { queryParams: { tab: 'main' } });
        } else {
          this.isLoading.model = true;
          this.productService.getProductInfo(result.id).subscribe(savedModel => {
            this.model = savedModel;
            this.isLoading.model = false;
            this.updateForm();
          });
        }
        this.isSaving = false;
      }, err => {
        this.isSaving = false;
        this.notification.error(this.tr.translate('nav.ProductDefinitions'), this.tr.translate(err.error?.message));
      });
    } else {
      Object.entries(this.form.controls).forEach(([key, control]) => {
        if (control.status !== 'VALID') {
          console.log('control is not valid', key, control);
        }
      });
      console.log('not valid form', this.form);
    }
  }

  private updateForm() {
    const formModel: Partial<FormModel> = {
      enabled: this.model.enabled,
      title: this.model.title,
      brandId: this.model.brandId,
      categoryId: this.model.categoryId,
      type: this.model.type,
      kind: this.model.kind,
      fullTitle: this.model.fullTitle,
      composition: this.model.composition,
      description: this.model.description,
      decored: this.model.decored,
      globalForSale: this.model.globalForSale,
      cfOutputProduct: this.model.cfOutputProduct,
      toCbInvoice: this.model.toCbInvoice,
      dynamicWeight: this.model.dynamicWeight,
      onlineSellable: this.model.onlineSellable,
      purchasable: this.model.purchasable,
      unlimited: this.model.unlimited,
      weight: this.model.weight ?? null,
      unitOfMeasureId: this.model.unitOfMeasureId,
      spendCategoryCode: this.model.spendCategoryCode,
      ntin: this.model.ntin,
      gtin: this.model.gtin
    };
    this.form.patchValue(formModel);
  }
}
